import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Lock, MapPin, Check, Loader2, Clock, Save, Timer, AlertTriangle, ExternalLink } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { formatKickoff, isLocked, isSameDay } from "@/lib/utils-app";
import { toast } from "sonner";
import { TeamFlag } from "./TeamFlag";
import { MatchDetailsLink } from "./MatchDetailDialog";
import type { MatchData } from "./MatchCard";

interface Props {
  match: MatchData;
  existing?: { id: string; predicted_home_score: number; predicted_away_score: number; points_awarded: number };
  leagueId: string;
  userId: string;
  onSaved?: () => void;
  countdown?: string;
  lockMinutesBefore?: number;
}

/** Mobile-friendly prediction form with large thumb targets. */
export const PredictionForm = ({ match, existing, leagueId, userId, onSaved, countdown, lockMinutesBefore = 0 }: Props) => {
  const { t } = useTranslation();
  const locked = isLocked(match.kickoff_at, lockMinutesBefore);
  const finished = match.home_score !== null && match.away_score !== null;
  const isToday = isSameDay(match.kickoff_at);
  const [h, setH] = useState<number>(existing?.predicted_home_score ?? 0);
  const [a, setA] = useState<number>(existing?.predicted_away_score ?? 0);
  const [busy, setBusy] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [uniqueError, setUniqueError] = useState<{home: number; away: number; takenBy?: string} | null>(null);
  const [localExisting, setLocalExisting] = useState(existing);
  const [lockCountdown, setLockCountdown] = useState<string>("");

  useEffect(() => {
    setLocalExisting(existing);
    setH(existing?.predicted_home_score ?? 0);
    setA(existing?.predicted_away_score ?? 0);
  }, [existing]);

  useEffect(() => {
    if (!isToday || locked || finished) return;
    const deadline = new Date(match.kickoff_at).getTime() - lockMinutesBefore * 60_000;
    const update = () => {
      const diff = deadline - Date.now();
      if (diff <= 0) { setLockCountdown(""); return; }
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      const s = Math.floor((diff % 60_000) / 1_000);
      if (h > 0) setLockCountdown(`${h}h ${m}m ${s}s`);
      else setLockCountdown(`${m}m ${s}s`);
    };
    update();
    const id = setInterval(update, 1_000);
    return () => clearInterval(id);
  }, [match.kickoff_at, lockMinutesBefore, isToday, locked, finished]);

  const doSave = async (homeScore: number, awayScore: number) => {
    setBusy(true);
    const payload = { league_id: leagueId, match_id: match.id, user_id: userId, predicted_home_score: homeScore, predicted_away_score: awayScore };
    // Always upsert on (league_id, match_id, user_id). Using update-by-id when
    // `localExisting` is set used to break in two ways:
    //   1) when the parent's cached predictions were stale (e.g. tip saved on
    //      another tab/device while this view was open), `localExisting` was
    //      undefined → insert path → 23505 duplicate-key toast,
    //   2) when `localExisting.id` referenced a row that had been replaced
    //      elsewhere, the update silently affected 0 rows.
    // Upsert solves both: it updates the existing row when the unique key
    // matches and inserts otherwise.
    let { data, error } = await supabase
      .from("predictions")
      .upsert(payload, { onConflict: "league_id,match_id,user_id" })
      .select();
    // Belt-and-suspenders: if the DB still raises a duplicate-key error
    // (e.g. because ON CONFLICT couldn't fire under some edge case), fall
    // back to fetching the existing row's id and updating it directly.
    if (error && (error.code === "23505" || /duplicate key/i.test(error.message ?? ""))) {
      const { data: row } = await supabase
        .from("predictions")
        .select("id")
        .eq("league_id", leagueId)
        .eq("match_id", match.id)
        .eq("user_id", userId)
        .maybeSingle();
      if (row?.id) {
        const retry = await supabase.from("predictions").update(payload).eq("id", row.id).select();
        data = retry.data;
        error = retry.error;
      }
    }
    setBusy(false);
    if (error) {
      if (error.hint === "unique_results_taken" || error.message?.includes("already taken by another player")) {
        // Look up who has this score
        let takenBy: string | undefined;
        const { data: clash } = await supabase
          .from("predictions")
          .select("user_id")
          .eq("league_id", leagueId)
          .eq("match_id", match.id)
          .eq("predicted_home_score", homeScore)
          .eq("predicted_away_score", awayScore)
          .neq("user_id", userId)
          .maybeSingle();
        if (clash?.user_id) {
          const { data: prof } = await supabase
            .from("profiles_public")
            .select("name")
            .eq("id", clash.user_id)
            .maybeSingle();
          takenBy = prof?.name || undefined;
        }
        setUniqueError({ home: homeScore, away: awayScore, takenBy });
        toast.error(takenBy ? t("league.uniqueResultsTakenBy", { name: takenBy, home: homeScore, away: awayScore }) : t("league.uniqueResultsTaken"));
      } else {
        toast.error(error.message);
      }
    } else {
      const saved = data?.[0];
      setLocalExisting({
        id: localExisting?.id || saved?.id,
        predicted_home_score: homeScore,
        predicted_away_score: awayScore,
        points_awarded: localExisting?.points_awarded || 0,
      });
      setShowSaved(true);
      setUniqueError(null);
      toast.success(t("league.tipSaved"));
      onSaved?.();
      setTimeout(() => setShowSaved(false), 2000);
    }
  };

  const save = () => doSave(h, a);

  const tryFix = (newH: number, newA: number) => {
    setH(newH);
    setA(newA);
    setUniqueError(null);
    doSave(newH, newA);
  };

  const Stepper = ({ value, set, disabled }: { value: number; set: (n: number) => void; disabled: boolean }) => (
    <div className="flex items-center gap-1">
      <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0 rounded-full text-xl"
        disabled={disabled || busy || value <= 0} onClick={() => set(Math.max(0, value - 1))}>−</Button>
      <Input type="number" inputMode="numeric" min={0} max={20} value={value}
        onChange={e => set(Math.max(0, Math.min(20, parseInt(e.target.value) || 0)))}
        disabled={disabled || busy}
        className="w-12 h-10 px-1 text-center text-lg font-bold tabular-nums" />
      <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0 rounded-full text-xl"
        disabled={disabled || busy || value >= 20} onClick={() => set(Math.min(20, value + 1))}>+</Button>
    </div>
  );

  const venue = [match.stadium, match.city].filter(Boolean).join(", ");

  return (
    <Card className={`rounded-2xl shadow-card transition-shadow hover:shadow-elevated ${finished ? "border-primary/30" : locked ? "bg-muted/40 border-border" : "border-border"}`}>
      <CardContent className="p-3 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
          <div className="flex items-center gap-2 min-w-0">
            {match.match_number != null && <span className="font-medium text-foreground/70">#{match.match_number}</span>}
            <span className="whitespace-nowrap">{formatKickoff(match.kickoff_at)}</span>
            {match.group_name && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 rounded-full">
                {t("phases.groupShort")} {match.group_name}
              </Badge>
            )}
            {countdown && (
              <Badge className="text-[10px] px-2 py-0 rounded-full bg-gradient-gold text-primary-foreground shadow-gold animate-pulse border-0">
                {countdown}
              </Badge>
            )}
            {localExisting && !finished && (
              <Badge variant="secondary" className="text-[10px] rounded-full inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/40">
                <Check className="h-3 w-3" />{t("league.alreadyTipped")}
              </Badge>
            )}
          </div>
          {finished ? (
            <Badge variant="outline" className="text-[10px] rounded-full border-primary/40 text-primary">{t("league.officialResult")}</Badge>
          ) : locked ? (
            <Badge variant="secondary" className="text-[10px] rounded-full inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />{t("league.kickoffPassed")}
            </Badge>
          ) : null}
        </div>


        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <TeamFlag team={match.home_team} size="md" />
            <span className="font-semibold text-sm truncate">{match.home_team}</span>
          </div>
          <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
            <span className="font-semibold text-sm truncate text-right">{match.away_team}</span>
            <TeamFlag team={match.away_team} size="md" />
          </div>
        </div>

        {finished ? (
          <div className="space-y-2 pt-1">
            <div className="flex items-center justify-center gap-3 rounded-xl bg-gradient-hero py-2.5 text-primary-foreground shadow-elevated">
              <span className="text-2xl font-bold tabular-nums">{match.home_score}</span>
              <span className="text-lg opacity-70">:</span>
              <span className="text-2xl font-bold tabular-nums">{match.away_score}</span>
            </div>
            <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-muted/40 px-3 py-2 text-sm">
              <span className="text-muted-foreground text-xs">{t("league.yourTip")}</span>
              {localExisting ? (
                <div className="flex items-center gap-2">
                  <span className="font-semibold tabular-nums">{localExisting.predicted_home_score} : {localExisting.predicted_away_score}</span>
                  <Badge
                    className={
                      localExisting.points_awarded >= 3
                        ? "bg-gradient-gold text-primary-foreground shadow-gold border-0 rounded-full"
                        : "rounded-full"
                    }
                    variant={localExisting.points_awarded >= 3 ? "default" : localExisting.points_awarded >= 1 ? "secondary" : "outline"}
                  >
                    {localExisting.points_awarded >= 3
                      ? `${t("league.exactHit")} +${localExisting.points_awarded}`
                      : localExisting.points_awarded >= 1
                        ? `${t("league.correctOutcome")} +${localExisting.points_awarded}`
                        : `${t("league.noPoints")} +0`}
                  </Badge>
                </div>
              ) : (
                <span className="text-xs italic text-muted-foreground">{t("league.noTip")}</span>
              )}
            </div>
          </div>
        ) : locked ? (
          <div className="space-y-2">
            <div className="flex items-center justify-center gap-2">
              <Stepper value={h} set={setH} disabled={true} />
              <span className="text-muted-foreground font-bold text-lg">:</span>
              <Stepper value={a} set={setA} disabled={true} />
            </div>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground bg-muted/30 rounded-md px-3 py-2">
              <Lock className="h-3 w-3" />
              <span>{t("league.tipsLocked")}</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <Stepper value={h} set={setH} disabled={locked} />
            <span className="text-muted-foreground font-bold text-lg">:</span>
            <Stepper value={a} set={setA} disabled={locked} />
          </div>
        )}

        {uniqueError && (
          <Alert variant="destructive" className="rounded-xl border-destructive/40 bg-destructive/10 py-2.5">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <AlertTitle className="text-sm">{uniqueError.takenBy ? t("league.uniqueResultsTakenBy", { name: uniqueError.takenBy, home: uniqueError.home, away: uniqueError.away }) : t("league.uniqueResultsTaken")}</AlertTitle>
            <AlertDescription className="flex flex-wrap items-center gap-2 pt-1">
              <div className="flex gap-2">
                {uniqueError.home < 20 && (
                  <Button variant="outline" size="sm" className="h-7 text-xs rounded-full border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => tryFix(uniqueError.home + 1, uniqueError.away)}>
                    {t("league.tryScore", { home: uniqueError.home + 1, away: uniqueError.away })}
                  </Button>
                )}
                {uniqueError.away < 20 && (
                  <Button variant="outline" size="sm" className="h-7 text-xs rounded-full border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => tryFix(uniqueError.home, uniqueError.away + 1)}>
                    {t("league.tryScore", { home: uniqueError.home, away: uniqueError.away + 1 })}
                  </Button>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {(
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            {venue && (
              <span className="flex items-center gap-1 min-w-0">
                <MapPin className="h-3 w-3 shrink-0" />
                <span className="truncate">{venue}</span>
              </span>
            )}
            <MatchDetailsLink matchId={match.id} />
          </div>
        )}


        {isToday && !locked && !finished && lockCountdown && (
          <div className="flex items-center justify-center gap-2 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40 px-3 py-2 text-xs">
            <Timer className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
            <span className="text-amber-700 dark:text-amber-300 font-medium">
              {t("league.locksIn")} {lockCountdown}
            </span>
            <span className="text-amber-600/70 dark:text-amber-400/60">
              ({t("league.deadline")} {new Date(new Date(match.kickoff_at).getTime() - lockMinutesBefore * 60_000).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" })})
            </span>
          </div>
        )}

        {!locked && !finished && (
          <div className="flex items-center gap-2">
            <Button onClick={save} disabled={busy} className="flex-1 h-10 rounded-full bg-gradient-hero text-primary-foreground shadow-gold hover:opacity-95 border-0">
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
              <>
                  <Save className="h-4 w-4 mr-2" />
                  {localExisting ? t("league.changeTip") : t("common.save")}
                </>
              )}
            </Button>
            {showSaved && (
              <Badge variant="default" className="h-10 px-3 rounded-full bg-gradient-gold text-primary-foreground border-0 shadow-gold">
                <Check className="h-3.5 w-3.5 mr-1" />{t("league.saved")}
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
